import { CONSOLE_ONLY } from 'error-handling'
import type Rollbar from 'rollbar'
import {
  FUNDRAISING_ERROR_KEY,
  type FundraisingErrorReporter,
} from 'fundraising-ui/composables/useFundraisingError'
import { bakedMessages } from 'fundraising-ui/baked'
import { mergeFundraisingFallback } from '~/libs/fundraisingI18n'
import { reportFundraisingError } from '~/libs/fundraisingErrorReport'
import { useFundraisingForm } from '~/composables/useFundraisingForm'

// Feed the fundraising-ui islands their strings and expose the translator they inject:
//   - the islands' own strings are baked at build (fundraisingBake in nuxt.config.ts); a build
//     without the bake merges the catalogs into vue-i18n under `fundraising.*` instead
//   - provide('t', ...) is what `inject('t')` inside the fundraising-ui islands resolves to
//   - provide('onFundraisingError', ...) sends the islands' failures to Rollbar
export default defineNuxtPlugin(async nuxtApp => {
  // The islands hand up an error and a context and leave reporting to the host; unwired, they only
  // log to the console. The package has already reduced the error to a reportable shape (no donor
  // data), so it goes to Rollbar as-is. `$rollbar` is read per call: the module's plugin may register
  // after this one. Server-side, flagError's own server-token instance is the sink.
  const form = useFundraisingForm()
  const onFundraisingError: FundraisingErrorReporter = (error, context) => {
    const sink = import.meta.server
      ? undefined
      : ((nuxtApp.$rollbar as Rollbar | null | undefined) ?? CONSOLE_ONLY)
    reportFundraisingError(error, context, form.value, sink)
  }
  nuxtApp.vueApp.provide(FUNDRAISING_ERROR_KEY, onFundraisingError)
  // Only /gava names its form so far; any other page must not report under the last one's id.
  useRouter().beforeEach((to, from) => {
    if (to.path !== from.path) form.value = { formId: '', formLang: '' }
  })

  const i18n = nuxtApp.$i18n as {
    t: (key: string, named?: Record<string, unknown>) => string
    mergeLocaleMessage: (locale: string, message: Record<string, unknown>) => void
  }

  await mergeFundraisingFallback(bakedMessages, (locale, message) =>
    i18n.mergeLocaleMessage(locale, message),
  )

  nuxtApp.vueApp.provide('t', (key: string, named?: Record<string, unknown>) =>
    i18n.t(key, named ?? {}),
  )
})
